import {useMemo, useEffect, useRef, useState, useSyncExternalStore} from 'react'
import * as THREE from 'three'
import {useLoader} from '@react-three/fiber'

import {DecalGeometry} from 'three/addons/geometries/DecalGeometry.js'
import {
  createRoughnessNoiseMap,
  createBrushedMetalNormalMap,
  createNoiseNormalMap,
} from '../../utils/proceduralTextures'
import type {
  CameraLensFeature,
  DisplayCutoutFeature,
  HomeIndicatorFeature,
  FrontSpeakerFeature,
  SideEdgeFeatures,
  SideButtonConfig,
  MuteSwitchConfig,
  AntennaConfig,
  SpeakerGrillConfig,
  ScrewConfig,
  SimCardSlotConfig,
  ChargingConnectorConfig,
  HomeButtonConfig,
  FrontSpeakerConfig,
} from '../types'
import {DISPLAY_EXTRUDE, FRONT_FACE_GAP, SHADOW_INSERT_COLOR, SHADOW_INSERT_OPACITY} from '../workerGeo/helpers'
import {reportError} from '../../errors/reportError'
import {loadSceneImage} from '../../utils/sceneImageLoader'
import {useDeviceGeometry} from './useDeviceGeometry'
import {isLowPolyThumbnail} from './thumbnailGeometryMode'
import DeviceBodyGhostModel from './ghostModels/DeviceBodyGhostModel'
import FrontFaceGhostModels from './ghostModels/FrontFaceGhostModels'
import FrontSpeakerGhostModel from './ghostModels/FrontSpeakerGhostModel'
import HomeButtonGhostModel from './ghostModels/HomeButtonGhostModel'
import SideEdgeGhostModel from './ghostModels/SideEdgeGhostModel'
import DecalGhostModels from './ghostModels/DecalGhostModels'
import {getPreviewSnapshot, subscribePreview} from './previewDimensions'
import {getPreviewFrontFaceSnapshot, subscribePreviewFrontFace} from './previewFrontFace'
import {getPreviewFrontSpeakerSnapshot, subscribePreviewFrontSpeaker} from './previewFrontSpeaker'
import {getPreviewHomeButtonSnapshot, subscribePreviewHomeButton} from './previewHomeButton'
import {getPreviewSideEdgeSnapshot, subscribePreviewSideEdge} from './previewSideEdge'
import {getRemovedSideEdgeSnapshot, subscribeRemovedSideEdge} from './removedSideEdgeStore'
import {getRemovedFrontFeatureSnapshot, subscribeRemovedFrontFeature} from './removedFrontFeatureStore'
import {getPreviewDecalFeatureSnapshot, subscribePreviewDecalFeature} from './previewDecalFeatures'
import {useAppSelector} from '../../store'
import {selectSceneByIndex} from '../store/scenesSlice'
import {useEditorConfig} from '../../hooks/useEditorConfig'

/** Create a canvas texture with a rounded rectangle for use as an alpha map */
const createRoundedRectAlphaMap = (w: number, h: number, r: number): THREE.CanvasTexture => {
  const res = 256
  const canvas = document.createElement('canvas')
  canvas.width = res
  canvas.height = Math.round(res * (h / w))
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#000'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // Scale corner radius to canvas pixels
  const rx = (r / w) * canvas.width
  ctx.fillStyle = '#fff'
  ctx.beginPath()
  ctx.roundRect(0, 0, canvas.width, canvas.height, rx)
  ctx.fill()

  const tex = new THREE.CanvasTexture(canvas)
  return tex
}

/** Create a circular alpha map for the camera lens decal */
const createCircleAlphaMap = (res: number): THREE.CanvasTexture => {
  const canvas = document.createElement('canvas')
  canvas.width = res
  canvas.height = res
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#000'
  ctx.fillRect(0, 0, res, res)
  ctx.fillStyle = '#fff'
  ctx.beginPath()
  ctx.arc(res / 2, res / 2, res / 2, 0, Math.PI * 2)
  ctx.fill()
  return new THREE.CanvasTexture(canvas)
}

/** Create a procedural camera lens texture with concentric rings and subtle reflections */
const createCameraLensTexture = (res: number): THREE.CanvasTexture => {
  const canvas = document.createElement('canvas')
  canvas.width = res
  canvas.height = res
  const ctx = canvas.getContext('2d')!
  const cx = res / 2
  const cy = res / 2
  const r = res / 2

  // Outer ring — dark metallic bezel
  const outerGrad = ctx.createRadialGradient(cx, cy, r * 0.85, cx, cy, r)
  outerGrad.addColorStop(0, '#2a2a2a')
  outerGrad.addColorStop(0.5, '#1a1a1a')
  outerGrad.addColorStop(1, '#111111')
  ctx.fillStyle = outerGrad
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fill()

  // Inner lens glass — dark with subtle color gradient
  const innerGrad = ctx.createRadialGradient(cx * 0.9, cy * 0.85, 0, cx, cy, r * 0.82)
  innerGrad.addColorStop(0, '#1c1e2a')
  innerGrad.addColorStop(0.4, '#12141d')
  innerGrad.addColorStop(0.7, '#0a0c14')
  innerGrad.addColorStop(1, '#060810')
  ctx.fillStyle = innerGrad
  ctx.beginPath()
  ctx.arc(cx, cy, r * 0.82, 0, Math.PI * 2)
  ctx.fill()

  // Subtle highlight arc — simulates light reflection
  ctx.strokeStyle = 'rgba(120, 140, 200, 0.15)'
  ctx.lineWidth = r * 0.06
  ctx.beginPath()
  ctx.arc(cx, cy, r * 0.6, -Math.PI * 0.8, -Math.PI * 0.2)
  ctx.stroke()

  // Center dot — small bright spot
  const dotGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 0.08)
  dotGrad.addColorStop(0, 'rgba(180, 190, 220, 0.25)')
  dotGrad.addColorStop(1, 'rgba(0, 0, 0, 0)')
  ctx.fillStyle = dotGrad
  ctx.beginPath()
  ctx.arc(cx, cy, r * 0.08, 0, Math.PI * 2)
  ctx.fill()

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// ── DeviceMeshV2 — CSG-based device with texture on display ───────

const DeviceMeshV2 = ({
  sceneIndex,
  textureUrl,
  onLoadingChange,
  onWorkerBusyChange,
}: {
  sceneIndex: number
  textureUrl: string
  onLoadingChange?: (loading: boolean) => void
  onWorkerBusyChange?: (busy: boolean) => void
}) => {
  const sceneState = useAppSelector(selectSceneByIndex(sceneIndex))
  const {deviceConfig} = useEditorConfig()

  const [texture, setTexture] = useState<THREE.Texture | null>(null)

  const screwTexture = useLoader(THREE.TextureLoader, '/textures/screw-head.png')
  const grillTexture = useLoader(THREE.TextureLoader, '/textures/speaker-grill.jpg')
  /* eslint-disable react-hooks/immutability -- standard Three.js texture setup */
  grillTexture.wrapS = THREE.RepeatWrapping
  grillTexture.wrapT = THREE.RepeatWrapping
  grillTexture.repeat.set(1, 1)
  /* eslint-enable react-hooks/immutability */

  // Merge Redux dimensions into base config for dynamic device sizing
  const deviceWidth = sceneState.deviceWidth
  const deviceHeight = sceneState.deviceHeight
  const deviceDepth = sceneState.deviceDepth
  const deviceCornerRadius = sceneState.deviceCornerRadius
  const deviceEdgeBevel = sceneState.deviceEdgeBevel
  const frontFaceInset = sceneState.frontFaceInset
  const displayCornerRadius = sceneState.displayCornerRadius
  const displayInsetX = sceneState.displayInsetX
  const displayInsetY = sceneState.displayInsetY
  const frontFaceFeatures = sceneState.frontFaceFeatures
  const sideEdgeFeatures = sceneState.sideEdgeFeatures
  const geometryVersion = sceneState.geometryVersion

  // Load screen image with "cover" fit onto a canvas matching display aspect ratio:
  // image preserves aspect and is cropped to fill — no letterbox bars, no deformation.
  useEffect(() => {
    const handleLoad = (img: HTMLImageElement) => {
      const clampedBevel = Math.min(deviceEdgeBevel, deviceWidth / 2, deviceHeight / 2, deviceDepth / 2)
      const maxPlaneZ = deviceDepth / 2 - 0.01
      const xyInset = Math.max(0, frontFaceInset - maxPlaneZ)
      const fw = Math.max(0.01, deviceWidth - 2 * clampedBevel - 2 * xyInset - 2 * FRONT_FACE_GAP)
      const fh = Math.max(0.01, deviceHeight - 2 * clampedBevel - 2 * xyInset - 2 * FRONT_FACE_GAP)
      const dispW = Math.max(0.01, fw - 2 * displayInsetX)
      const dispH = Math.max(0.01, fh - 2 * displayInsetY)
      const dispAspect = dispW / dispH
      const imgAspect = img.naturalWidth / img.naturalHeight

      // Size canvas to display aspect; pick the larger of the two dimensions to preserve resolution.
      let canvasW: number
      let canvasH: number
      if (imgAspect > dispAspect) {
        // Image is wider than display — crop horizontally; canvas height matches image.
        canvasH = img.naturalHeight
        canvasW = Math.max(1, Math.round(canvasH * dispAspect))
      } else {
        // Image is taller than display — crop vertically; canvas width matches image.
        canvasW = img.naturalWidth
        canvasH = Math.max(1, Math.round(canvasW / dispAspect))
      }

      const canvas = document.createElement('canvas')
      canvas.width = canvasW
      canvas.height = canvasH
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // Draw image at natural size, centered — overflow is clipped by canvas bounds (cover).
      const drawX = Math.round((canvasW - img.naturalWidth) / 2)
      const drawY = Math.round((canvasH - img.naturalHeight) / 2)
      ctx.drawImage(img, drawX, drawY, img.naturalWidth, img.naturalHeight)

      const t = new THREE.CanvasTexture(canvas)
      t.colorSpace = THREE.SRGBColorSpace
      setTexture(t)
    }
    return loadSceneImage(textureUrl, handleLoad, (failure) => {
      // Screen image download failed — leave the device without a screen
      // texture rather than blocking on a load that never completes.
      reportError(new Error('Screen image failed to load'), 'screenImage.loadError', {url: textureUrl, ...failure})
    })
  }, [
    textureUrl,
    deviceWidth,
    deviceHeight,
    deviceDepth,
    deviceEdgeBevel,
    frontFaceInset,
    displayInsetX,
    displayInsetY,
  ])

  // Split features: only homeButton and frontSpeaker affect CSG geometry (worker)
  // displayCutout, cameraLens, homeIndicator are decal-only — changes skip the worker
  // Use JSON.stringify to keep stable reference when only decal features change
  const csgFeaturesJson = useMemo(
    () => JSON.stringify(frontFaceFeatures.filter((f) => f.type === 'homeButton' || f.type === 'frontSpeaker')),
    [frontFaceFeatures],
  )
  const csgFeaturesRef = useRef(csgFeaturesJson)
  const csgFeatures = useMemo(() => {
    if (csgFeaturesRef.current !== csgFeaturesJson) {
      csgFeaturesRef.current = csgFeaturesJson
    }
    return JSON.parse(csgFeaturesRef.current) as typeof frontFaceFeatures
  }, [csgFeaturesJson])

  // ── Convert sideEdgeFeatures to flat worker arrays ──────────────
  const derivedSideArrays = useMemo(() => {
    const allFeatures = Object.entries(sideEdgeFeatures) as [string, SideEdgeFeatures[keyof SideEdgeFeatures]][]
    const sideButtons: SideButtonConfig[] = []
    const muteSwitches: MuteSwitchConfig[] = []
    const antennas: AntennaConfig[] = []
    const speakerGrills: SpeakerGrillConfig[] = []
    const screws: ScrewConfig[] = []
    const chargingConnectors: ChargingConnectorConfig[] = []
    const simCardSlots: SimCardSlotConfig[] = []

    for (const [edge, features] of allFeatures) {
      const side = edge as 'left' | 'right' | 'top' | 'bottom'
      for (const f of features) {
        switch (f.type) {
          case 'sideButton':
            sideButtons.push({
              side,
              position: f.position,
              width: f.width,
              height: f.height,
              cornerRadius: f.cornerRadius,
              depth: f.depth ?? 0.5,
              chamfer: f.chamfer ?? 0,
            })
            break
          case 'muteSwitch':
            muteSwitches.push({
              side,
              position: f.position,
              width: f.width,
              height: f.height,
              cornerRadius: f.cornerRadius,
            })
            break
          case 'antenna':
            antennas.push({
              edge: side,
              position: f.position,
              height: f.width,
              overflow: 1.5,
            })
            break
          case 'sideSpeakerGrill':
            speakerGrills.push({
              side,
              x: f.position,
              width: f.width,
              height: f.height,
              cornerRadius: f.cornerRadius,
            })
            break
          case 'screw':
            screws.push({
              side,
              x: f.position,
              diameter: f.diameter,
              pattern: f.pattern,
            })
            break
          case 'chargingConnector':
            chargingConnectors.push({
              side,
              position: f.position,
              width: f.width,
              height: f.height,
              cornerRadius: f.cornerRadius,
            })
            break
          case 'simCardSlot':
            simCardSlots.push({
              side,
              position: f.position,
              width: f.width,
              height: f.height,
              cornerRadius: f.cornerRadius,
              pinHoleOffset: f.pinHoleOffset,
            })
            break
        }
      }
    }
    return {sideButtons, muteSwitches, antennas, speakerGrills, screws, chargingConnectors, simCardSlots}
  }, [sideEdgeFeatures])

  // Extract homeButton & frontSpeaker arrays from CSG features for the worker
  const derivedFrontArrays = useMemo(() => {
    const homeButtons: HomeButtonConfig[] = csgFeatures
      .filter((f): f is HomeButtonConfig & {id: string; type: 'homeButton'} => f.type === 'homeButton')
      .map(({radius, x, y}) => ({radius, x, y}))
    const frontSpeakers: FrontSpeakerConfig[] = csgFeatures
      .filter((f): f is FrontSpeakerConfig & {id: string; type: 'frontSpeaker'} => f.type === 'frontSpeaker')
      .map(({width, height, cornerRadius, xOffset, yOffset}) => ({width, height, cornerRadius, xOffset, yOffset}))
    return {homeButtons, frontSpeakers}
  }, [csgFeatures])

  // Worker config — only includes CSG-affecting features
  const workerConfig = useMemo(
    () => ({
      body: {
        width: deviceWidth,
        height: deviceHeight,
        depth: deviceDepth,
        cornerRadius: deviceCornerRadius,
        edgeBevel: deviceEdgeBevel,
      },
      frontFace: {
        inset: frontFaceInset,
      },
      display: {
        cornerRadius: displayCornerRadius,
        insetX: displayInsetX,
        insetY: displayInsetY,
      },
      homeButtons: derivedFrontArrays.homeButtons,
      frontSpeakers: derivedFrontArrays.frontSpeakers,
      sideButtons: derivedSideArrays.sideButtons,
      muteSwitches: derivedSideArrays.muteSwitches,
      antennas: derivedSideArrays.antennas,
      speakerGrills: derivedSideArrays.speakerGrills,
      screws: derivedSideArrays.screws,
      chargingConnectors: derivedSideArrays.chargingConnectors,
      simCardSlots: derivedSideArrays.simCardSlots,
      // Reduced tessellation only for thumbnails (set by the render app); editor
      // + full-size renders keep full detail. Read once — it never changes after
      // the page loads, so it intentionally stays out of the dependency list.
      lowPoly: isLowPolyThumbnail(),
    }),
    [
      deviceWidth,
      deviceHeight,
      deviceDepth,
      deviceCornerRadius,
      deviceEdgeBevel,
      frontFaceInset,
      displayCornerRadius,
      displayInsetX,
      displayInsetY,
      derivedSideArrays,
      derivedFrontArrays,
    ],
  )

  // Decal feature arrays — extracted from full features list, don't trigger worker
  const cameraLenses = useMemo(
    () => frontFaceFeatures.filter((f): f is CameraLensFeature => f.type === 'cameraLens'),
    [frontFaceFeatures],
  )
  const displayCutouts = useMemo(
    () => frontFaceFeatures.filter((f): f is DisplayCutoutFeature => f.type === 'displayCutout'),
    [frontFaceFeatures],
  )
  const homeIndicators = useMemo(
    () => frontFaceFeatures.filter((f): f is HomeIndicatorFeature => f.type === 'homeIndicator'),
    [frontFaceFeatures],
  )
  const frontSpeakers = useMemo(
    () => frontFaceFeatures.filter((f): f is FrontSpeakerFeature => f.type === 'frontSpeaker'),
    [frontFaceFeatures],
  )

  const {geometries: geos, initialLoading, updating} = useDeviceGeometry(workerConfig)
  const devicePreviewActive = useSyncExternalStore(subscribePreview, getPreviewSnapshot) !== null
  const frontFacePreviewActive = useSyncExternalStore(subscribePreviewFrontFace, getPreviewFrontFaceSnapshot) !== null
  const frontSpeakerPreviewActive =
    useSyncExternalStore(subscribePreviewFrontSpeaker, getPreviewFrontSpeakerSnapshot) !== null
  const homeButtonPreviewActive =
    useSyncExternalStore(subscribePreviewHomeButton, getPreviewHomeButtonSnapshot) !== null
  const sideEdgePreviewActive = useSyncExternalStore(subscribePreviewSideEdge, getPreviewSideEdgeSnapshot) !== null
  const removedSideEdgeActive = useSyncExternalStore(subscribeRemovedSideEdge, getRemovedSideEdgeSnapshot) !== null
  const removedFrontFeatureActive =
    useSyncExternalStore(subscribeRemovedFrontFeature, getRemovedFrontFeatureSnapshot) !== null
  const decalPreviewActive = useSyncExternalStore(subscribePreviewDecalFeature, getPreviewDecalFeatureSnapshot) !== null

  // Track which preview type was last active so we show only the right ghost during worker updates.
  const lastPreviewRef = useRef<'device' | 'frontFace' | 'frontSpeaker' | 'homeButton' | 'sideEdge' | null>(null)
  if (devicePreviewActive) lastPreviewRef.current = 'device'
  if (frontFacePreviewActive) lastPreviewRef.current = 'frontFace'
  if (frontSpeakerPreviewActive) lastPreviewRef.current = 'frontSpeaker'
  if (homeButtonPreviewActive) lastPreviewRef.current = 'homeButton'
  if (sideEdgePreviewActive) lastPreviewRef.current = 'sideEdge'

  // Detect CSG feature additions/removals (no preview) to set lastPreviewRef correctly
  const prevCsgRef = useRef(csgFeaturesJson)
  if (prevCsgRef.current !== csgFeaturesJson && !frontSpeakerPreviewActive && !homeButtonPreviewActive) {
    const prev = JSON.parse(prevCsgRef.current) as {type: string}[]
    const curr = JSON.parse(csgFeaturesJson) as {type: string}[]
    if (curr.filter((f) => f.type === 'frontSpeaker').length !== prev.filter((f) => f.type === 'frontSpeaker').length)
      lastPreviewRef.current = 'frontSpeaker'
    if (curr.filter((f) => f.type === 'homeButton').length !== prev.filter((f) => f.type === 'homeButton').length)
      lastPreviewRef.current = 'homeButton'
  }
  prevCsgRef.current = csgFeaturesJson

  // Detect side edge feature additions/removals
  // Guard: don't override when dimension changes caused side features to be clamped as a side-effect
  const prevSideEdgeRef = useRef(JSON.stringify(sideEdgeFeatures))
  const sideEdgeJson = JSON.stringify(sideEdgeFeatures)
  if (prevSideEdgeRef.current !== sideEdgeJson && !devicePreviewActive && lastPreviewRef.current !== 'device') {
    lastPreviewRef.current = 'sideEdge'
  }
  prevSideEdgeRef.current = sideEdgeJson

  // Track pending side edge features: a feature is "pending" while the worker rebuilds
  // the geometry for an edit that touched it. Keyed map of serialized feature content so
  // we detect both ADDED and CHANGED features (slider commit, undo/redo, creation) — the
  // ghost then shows only the touched feature(s), not the whole set. Cleared on finish.
  const buildSideEdgeMap = () => {
    const map = new Map<string, string>()
    for (const edge of ['left', 'right', 'top', 'bottom'] as const) {
      for (const f of sideEdgeFeatures[edge]) map.set(`${edge}:${f.id}`, JSON.stringify(f))
    }
    return map
  }
  const prevSideEdgeMapRef = useRef(buildSideEdgeMap())
  const [pendingSideEdgeKeys, setPendingSideEdgeKeys] = useState<Set<string>>(new Set())

  // Detect added/changed features and add their keys to pending
  useEffect(() => {
    const prev = prevSideEdgeMapRef.current
    const currentMap = new Map<string, string>()
    const touched: string[] = []
    for (const edge of ['left', 'right', 'top', 'bottom'] as const) {
      for (const f of sideEdgeFeatures[edge]) {
        const key = `${edge}:${f.id}`
        const json = JSON.stringify(f)
        currentMap.set(key, json)
        if (prev.get(key) !== json) touched.push(key)
      }
    }
    prevSideEdgeMapRef.current = currentMap
    if (touched.length > 0) {
      setPendingSideEdgeKeys((prev) => {
        const next = new Set(prev)
        for (const k of touched) next.add(k)
        return next
      })
    }
  }, [sideEdgeFeatures])

  // Clear pending when worker finishes
  const prevUpdatingForPendingRef = useRef(updating)
  useEffect(() => {
    if (prevUpdatingForPendingRef.current && !updating && pendingSideEdgeKeys.size > 0) {
      setPendingSideEdgeKeys(new Set())
    }
    prevUpdatingForPendingRef.current = updating
  }, [updating, pendingSideEdgeKeys])

  const showDeviceGhost = devicePreviewActive || (updating && lastPreviewRef.current === 'device')
  const showFrontFaceGhost = frontFacePreviewActive || (updating && lastPreviewRef.current === 'frontFace')
  const showFrontSpeakerGhost =
    frontSpeakerPreviewActive || (updating && lastPreviewRef.current === 'frontSpeaker') || removedFrontFeatureActive
  const showHomeButtonGhost =
    homeButtonPreviewActive || (updating && lastPreviewRef.current === 'homeButton') || removedFrontFeatureActive
  const showSideEdgeGhost =
    sideEdgePreviewActive || (updating && lastPreviewRef.current === 'sideEdge') || removedSideEdgeActive

  const {body} = workerConfig
  const {materials} = deviceConfig

  // Read editable material properties from Redux store
  const bodyColor = sceneState.bodyColor
  const bodyMetalness = sceneState.bodyMetalness
  const bodyRoughness = sceneState.bodyRoughness
  const bodyClearcoat = sceneState.bodyClearcoat
  const bodyClearcoatRoughness = sceneState.bodyClearcoatRoughness
  const bodyReflectivity = sceneState.bodyReflectivity
  const bodyNoiseIntensity = sceneState.bodyNoiseIntensity
  const bodyNormalStrength = sceneState.bodyNormalStrength
  const bodyNormalPattern = sceneState.bodyNormalPattern

  // Ref for imperative texture updates (avoids material recreation per slider step)
  const bodyMatRef = useRef<THREE.MeshPhysicalMaterial>(null)

  // Procedural textures for body — regenerated when slider values change
  const bodyRoughnessMap = useMemo(() => {
    if (bodyNoiseIntensity <= 0) return null
    return createRoughnessNoiseMap(256, bodyNoiseIntensity)
  }, [bodyNoiseIntensity])

  const bodyNormalMap = useMemo(() => {
    if (bodyNormalStrength <= 0) return null
    return bodyNormalPattern === 'brushed'
      ? createBrushedMetalNormalMap(256, bodyNormalStrength)
      : createNoiseNormalMap(256, bodyNormalStrength)
  }, [bodyNormalStrength, bodyNormalPattern])

  const bodyNormalScale = useMemo(() => new THREE.Vector2(bodyNormalStrength, bodyNormalStrength), [bodyNormalStrength])

  // Dispose old textures on regeneration
  useEffect(() => {
    return () => {
      bodyRoughnessMap?.dispose()
    }
  }, [bodyRoughnessMap])

  useEffect(() => {
    return () => {
      bodyNormalMap?.dispose()
    }
  }, [bodyNormalMap])

  // Imperatively update textures on the material — avoids full key-based recreation
  useEffect(() => {
    if (!bodyMatRef.current) return
    bodyMatRef.current.roughnessMap = bodyNoiseIntensity > 0 ? bodyRoughnessMap : null
    bodyMatRef.current.normalMap = bodyNormalStrength > 0 ? bodyNormalMap : null
    bodyMatRef.current.normalScale.set(bodyNormalStrength, bodyNormalStrength)
    bodyMatRef.current.needsUpdate = true
  }, [bodyRoughnessMap, bodyNormalMap, bodyNoiseIntensity, bodyNormalStrength])

  // ── Buttons procedural textures ─────────────────────────────────
  const buttonsNoiseIntensity = sceneState.buttonsNoiseIntensity
  const buttonsNormalStrength = sceneState.buttonsNormalStrength
  const buttonsNormalPattern = sceneState.buttonsNormalPattern

  const buttonsRoughnessMap = useMemo(() => {
    if (buttonsNoiseIntensity <= 0) return null
    return createRoughnessNoiseMap(256, buttonsNoiseIntensity)
  }, [buttonsNoiseIntensity])

  const buttonsNormalMap = useMemo(() => {
    if (buttonsNormalStrength <= 0) return null
    return buttonsNormalPattern === 'brushed'
      ? createBrushedMetalNormalMap(256, buttonsNormalStrength)
      : createNoiseNormalMap(256, buttonsNormalStrength)
  }, [buttonsNormalStrength, buttonsNormalPattern])

  const buttonsNormalScale = useMemo(
    () => new THREE.Vector2(buttonsNormalStrength, buttonsNormalStrength),
    [buttonsNormalStrength],
  )

  useEffect(() => {
    return () => {
      buttonsRoughnessMap?.dispose()
    }
  }, [buttonsRoughnessMap])

  useEffect(() => {
    return () => {
      buttonsNormalMap?.dispose()
    }
  }, [buttonsNormalMap])

  const frontFaceColor = sceneState.frontFaceColor
  const frontFaceMetalness = sceneState.frontFaceMetalness
  const frontFaceRoughness = sceneState.frontFaceRoughness
  const frontFaceClearcoat = sceneState.frontFaceClearcoat
  const frontFaceClearcoatRoughness = sceneState.frontFaceClearcoatRoughness
  const frontFaceReflectivity = sceneState.frontFaceReflectivity
  const displayReflection = sceneState.displayReflection
  const buttonsColor = sceneState.buttonsColor
  const buttonsMetalness = sceneState.buttonsMetalness
  const buttonsRoughness = sceneState.buttonsRoughness
  const buttonsClearcoat = sceneState.buttonsClearcoat
  const buttonsClearcoatRoughness = sceneState.buttonsClearcoatRoughness
  const buttonsReflectivity = sceneState.buttonsReflectivity
  const antennasColor = sceneState.antennasColor
  const antennasMetalness = sceneState.antennasMetalness
  const antennasRoughness = sceneState.antennasRoughness
  const antennasClearcoat = sceneState.antennasClearcoat
  const antennasClearcoatRoughness = sceneState.antennasClearcoatRoughness
  const antennasReflectivity = sceneState.antennasReflectivity
  const screwsColor = sceneState.screwsColor
  const screwsMetalness = sceneState.screwsMetalness
  const screwsRoughness = sceneState.screwsRoughness
  const screwsClearcoat = sceneState.screwsClearcoat
  const screwsClearcoatRoughness = sceneState.screwsClearcoatRoughness
  const screwsReflectivity = sceneState.screwsReflectivity
  const frontSpeakerColor = sceneState.frontSpeakerColor
  const frontSpeakerMetalness = sceneState.frontSpeakerMetalness
  const frontSpeakerRoughness = sceneState.frontSpeakerRoughness
  const frontSpeakerClearcoat = sceneState.frontSpeakerClearcoat
  const frontSpeakerClearcoatRoughness = sceneState.frontSpeakerClearcoatRoughness
  const frontSpeakerReflectivity = sceneState.frontSpeakerReflectivity
  const speakerGrillColor = sceneState.speakerGrillColor
  const speakerGrillMetalness = sceneState.speakerGrillMetalness
  const speakerGrillRoughness = sceneState.speakerGrillRoughness
  const speakerGrillClearcoat = sceneState.speakerGrillClearcoat
  const speakerGrillClearcoatRoughness = sceneState.speakerGrillClearcoatRoughness
  const speakerGrillReflectivity = sceneState.speakerGrillReflectivity
  const ccTongueColor = sceneState.chargingConnectorTongueColor
  const ccTongueMetalness = sceneState.chargingConnectorTongueMetalness
  const ccTongueRoughness = sceneState.chargingConnectorTongueRoughness
  const ccTongueClearcoat = sceneState.chargingConnectorTongueClearcoat
  const ccTongueClearcoatRoughness = sceneState.chargingConnectorTongueClearcoatRoughness
  const ccTongueReflectivity = sceneState.chargingConnectorTongueReflectivity
  const ccRimColor = sceneState.chargingConnectorRimColor
  const ccRimMetalness = sceneState.chargingConnectorRimMetalness
  const ccRimRoughness = sceneState.chargingConnectorRimRoughness
  const ccRimClearcoat = sceneState.chargingConnectorRimClearcoat
  const ccRimClearcoatRoughness = sceneState.chargingConnectorRimClearcoatRoughness
  const ccRimReflectivity = sceneState.chargingConnectorRimReflectivity

  // Detect device preset switch — geometryVersion bumps trigger full loading overlay
  const prevGeoVersionRef = useRef(geometryVersion)
  const [presetSwitching, setPresetSwitching] = useState(false)
  useEffect(() => {
    if (prevGeoVersionRef.current !== geometryVersion) {
      prevGeoVersionRef.current = geometryVersion
      setPresetSwitching(true)
    }
  }, [geometryVersion])
  // Clear preset switching flag when worker finishes
  useEffect(() => {
    if (presetSwitching && !updating) {
      setPresetSwitching(false)
    }
  }, [presetSwitching, updating])

  // Show full overlay on initial load or device preset switch
  useEffect(() => {
    onLoadingChange?.(initialLoading || presetSwitching)
  }, [initialLoading, presetSwitching, onLoadingChange])

  // Report worker busy state — covers all worker activity (initial, update, preset switch)
  useEffect(() => {
    onWorkerBusyChange?.(initialLoading || updating)
  }, [initialLoading, updating, onWorkerBusyChange])

  const frontZ = body.depth / 2
  const displaySurfaceZ = frontZ + DISPLAY_EXTRUDE / 2

  // ── Temporary plane for decal projection ─────────────────────────
  // frontFaceGeo has the display cutout (hole), displayGeo is too small.
  // A simple plane covering the full front face provides a clean projection
  // surface so decals are not clipped at either boundary.
  const decalProjectionMesh = useMemo(() => {
    // Projection surface spans the FULL flat front (out to where the edge bevel starts),
    // not just the recessed front-face panel — so decals (camera lens, cutout, home
    // indicator) can be positioned anywhere on the front face without being clipped.
    const clampedBevel = Math.min(body.edgeBevel, body.width / 2, body.height / 2, body.depth / 2)
    const fw = Math.max(0.01, body.width - 2 * clampedBevel)
    const fh = Math.max(0.01, body.height - 2 * clampedBevel)
    const plane = new THREE.PlaneGeometry(fw, fh)
    const mesh = new THREE.Mesh(plane)
    mesh.position.set(0, 0, displaySurfaceZ + 0.05)
    mesh.updateMatrixWorld(true)
    return mesh
  }, [body.width, body.height, body.depth, body.edgeBevel, displaySurfaceZ])

  // Build DecalGeometry for each display cutout — projected onto front face plane
  const cutoutDecals = useMemo(() => {
    return displayCutouts.map((dc) => {
      const position = new THREE.Vector3(dc.xOffset, dc.yOffset, displaySurfaceZ + 0.1)
      const orientation = new THREE.Euler(0, 0, 0)
      const size = new THREE.Vector3(dc.width, dc.height, 1)
      const geo = new DecalGeometry(decalProjectionMesh, position, orientation, size)
      const alphaMap = createRoundedRectAlphaMap(dc.width, dc.height, dc.cornerRadius)
      return {id: dc.id, geo, alphaMap}
    })
  }, [decalProjectionMesh, displayCutouts, displaySurfaceZ])

  // Build DecalGeometry for each camera lens — projected onto front face plane
  const lensDecals = useMemo(() => {
    return cameraLenses.map((cl) => {
      const d = cl.radius * 2
      const position = new THREE.Vector3(cl.xOffset, cl.yOffset, displaySurfaceZ + 0.12)
      const orientation = new THREE.Euler(0, 0, 0)
      const size = new THREE.Vector3(d, d, 1)
      const geo = new DecalGeometry(decalProjectionMesh, position, orientation, size)
      const alphaMap = createCircleAlphaMap(256)
      const tex = createCameraLensTexture(512)
      return {id: cl.id, geo, alphaMap, texture: tex}
    })
  }, [decalProjectionMesh, cameraLenses, displaySurfaceZ])

  // Build DecalGeometry for each home indicator — projected onto front face plane
  const indicatorDecals = useMemo(() => {
    return homeIndicators.map((hi) => {
      const position = new THREE.Vector3(0, hi.yOffset, displaySurfaceZ + 0.08)
      const orientation = new THREE.Euler(0, 0, 0)
      const size = new THREE.Vector3(hi.width, hi.height, 1)
      const geo = new DecalGeometry(decalProjectionMesh, position, orientation, size)
      const alphaMap = createRoundedRectAlphaMap(hi.width, hi.height, hi.cornerRadius)
      const color = hi.dark ? '#333333' : '#e0e0e0'
      return {id: hi.id, geo, alphaMap, color}
    })
  }, [decalProjectionMesh, homeIndicators, displaySurfaceZ])

  if (!geos) return null

  const {
    bodyGeo,
    frontFaceGeo,
    displayGeo,
    antennaGeos,
    sideBtnGeos,
    sideBtnSlotGeos,
    portShellGeos,
    portTongueGeos,
    portInsertGeos,
    frontSpeakerInsertGeos,
    frontSpeakerDiscGeos,
    homeButtonPlugGeos,
    speakerGrillDiscGeos,
    speakerGrillSlotGeos,
    screwDiscGeos,
    simCardSlotTrayGeos,
    simCardSlotSlotGeos,
    muteSwitchInsertGeos,
    muteSwitchSlotGeos,
  } = geos

  return (
    <group visible={!presetSwitching}>
      {/* Ghost preview — device body, shown during device dimension slider drag or worker update */}
      {showDeviceGhost && <DeviceBodyGhostModel />}
      {/* Ghost preview — front face, shown during front face slider drag or worker update */}
      {showFrontFaceGhost && geos && <FrontFaceGhostModels />}
      {/* Ghost preview — front speaker, shown during speaker slider drag or worker update */}
      {showFrontSpeakerGhost && <FrontSpeakerGhostModel updating={updating} />}
      {/* Ghost preview — home button, shown during home button slider drag or worker update */}
      {showHomeButtonGhost && <HomeButtonGhostModel updating={updating} />}
      {/* Ghost preview — side edge features, shown during side edge slider drag or worker update */}
      {showSideEdgeGhost && <SideEdgeGhostModel updating={updating} pendingKeys={pendingSideEdgeKeys} />}
      {/* Ghost preview — decal features, shown during decal slider/pad drag */}
      {decalPreviewActive && <DecalGhostModels />}

      {/* Body */}
      <mesh geometry={bodyGeo} castShadow receiveShadow>
        <meshPhysicalMaterial
          ref={bodyMatRef}
          key={`body-${bodyNoiseIntensity > 0}-${bodyNormalStrength > 0}-${bodyNormalPattern}`}
          color={bodyColor}
          metalness={bodyMetalness}
          roughness={bodyRoughness}
          envMapIntensity={materials.body.envMapIntensity}
          clearcoat={bodyClearcoat}
          clearcoatRoughness={bodyClearcoatRoughness}
          reflectivity={bodyReflectivity}
          specularIntensity={1}
          roughnessMap={bodyNoiseIntensity > 0 ? bodyRoughnessMap : null}
          normalMap={bodyNormalStrength > 0 ? bodyNormalMap : null}
          normalScale={bodyNormalScale}
        />
      </mesh>

      {/* Antenna bands */}
      {antennaGeos.map((geo, i) => (
        <mesh key={i} geometry={geo} castShadow receiveShadow>
          <meshPhysicalMaterial
            color={antennasColor}
            metalness={antennasMetalness}
            roughness={antennasRoughness}
            clearcoat={antennasClearcoat}
            clearcoatRoughness={antennasClearcoatRoughness}
            reflectivity={antennasReflectivity}
            specularIntensity={1}
          />
        </mesh>
      ))}

      {/* Front face — geometry already in world coordinates from CSG intersection */}
      <group>
        <mesh geometry={frontFaceGeo} castShadow receiveShadow>
          <meshPhysicalMaterial
            color={frontFaceColor}
            metalness={frontFaceMetalness}
            roughness={frontFaceRoughness}
            clearcoat={frontFaceClearcoat}
            clearcoatRoughness={frontFaceClearcoatRoughness}
            reflectivity={frontFaceReflectivity}
            envMapIntensity={materials.frontFace.envMapIntensity}
            polygonOffset
            polygonOffsetFactor={-1}
            polygonOffsetUnits={-1}
          />
        </mesh>
      </group>

      {/* Home button plugs — front-face material plug carved into body+frontFace,
          guarantees the depression bottom always reads as front face regardless of front face thickness */}
      {homeButtonPlugGeos.map((geo, i) => (
        <mesh key={`hb-plug-${i}`} geometry={geo} castShadow receiveShadow>
          <meshPhysicalMaterial
            color={frontFaceColor}
            metalness={frontFaceMetalness}
            roughness={frontFaceRoughness}
            clearcoat={frontFaceClearcoat}
            clearcoatRoughness={frontFaceClearcoatRoughness}
            reflectivity={frontFaceReflectivity}
            envMapIntensity={materials.frontFace.envMapIntensity}
            polygonOffset
            polygonOffsetFactor={-1}
            polygonOffsetUnits={-1}
          />
        </mesh>
      ))}

      {/* Display — texture as emission (unlit at displayReflection=0) + clearcoat glass that fades in with displayReflection */}
      <group>
        <mesh geometry={displayGeo} castShadow receiveShadow>
          <meshPhysicalMaterial
            color="#000000"
            emissiveMap={texture}
            emissive="#ffffff"
            emissiveIntensity={1}
            toneMapped={false}
            clearcoat={displayReflection}
            clearcoatRoughness={0.05}
            envMapIntensity={displayReflection}
            metalness={0}
            roughness={1}
          />
        </mesh>
      </group>

      {/* Display cutouts — Dynamic Islands (middle decal layer) */}
      {cutoutDecals.map((decal) => (
        <mesh key={decal.id} geometry={decal.geo} renderOrder={2}>
          <meshPhysicalMaterial
            color={materials.displayCutout.color}
            metalness={materials.displayCutout.metalness}
            roughness={materials.displayCutout.roughness}
            clearcoat={materials.displayCutout.clearcoat}
            clearcoatRoughness={materials.displayCutout.clearcoatRoughness}
            reflectivity={materials.displayCutout.reflectivity}
            envMapIntensity={materials.displayCutout.envMapIntensity}
            alphaMap={decal.alphaMap}
            transparent
            alphaTest={0.5}
            polygonOffset
            polygonOffsetFactor={-4}
            polygonOffsetUnits={-4}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Camera lenses — topmost decal layer */}
      {lensDecals.map((decal) => (
        <mesh key={decal.id} geometry={decal.geo} renderOrder={3}>
          <meshPhysicalMaterial
            map={decal.texture}
            color="#ffffff"
            metalness={materials.cameraLens.metalness}
            roughness={materials.cameraLens.roughness}
            clearcoat={materials.cameraLens.clearcoat}
            clearcoatRoughness={materials.cameraLens.clearcoatRoughness}
            reflectivity={materials.cameraLens.reflectivity}
            envMapIntensity={materials.cameraLens.envMapIntensity}
            alphaMap={decal.alphaMap}
            transparent
            alphaTest={0.5}
            toneMapped={false}
            polygonOffset
            polygonOffsetFactor={-6}
            polygonOffsetUnits={-6}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Home indicator bars — bottommost decal layer */}
      {indicatorDecals.map((decal) => (
        <mesh key={decal.id} geometry={decal.geo} renderOrder={1}>
          <meshPhysicalMaterial
            color={decal.color}
            metalness={materials.homeIndicator.metalness}
            roughness={materials.homeIndicator.roughness}
            clearcoat={materials.homeIndicator.clearcoat}
            clearcoatRoughness={materials.homeIndicator.clearcoatRoughness}
            reflectivity={materials.homeIndicator.reflectivity}
            envMapIntensity={materials.homeIndicator.envMapIntensity}
            alphaMap={decal.alphaMap}
            transparent
            alphaTest={0.5}
            polygonOffset
            polygonOffsetFactor={-2}
            polygonOffsetUnits={-2}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Front speaker inserts — flat panels with speaker slits */}
      {frontSpeakerInsertGeos.map((geo, i) => (
        <group key={frontSpeakers[i]?.id ?? i}>
          <mesh geometry={geo} castShadow receiveShadow>
            <meshPhysicalMaterial
              color={frontSpeakerColor}
              metalness={frontSpeakerMetalness}
              roughness={frontSpeakerRoughness}
              clearcoat={frontSpeakerClearcoat}
              clearcoatRoughness={frontSpeakerClearcoatRoughness}
              reflectivity={frontSpeakerReflectivity}
              specularIntensity={1}
              side={THREE.DoubleSide}
            />
          </mesh>
        </group>
      ))}

      {/* Front speaker grill discs — textured with grill pattern */}
      {frontSpeakerDiscGeos.map((geo, i) => (
        <group key={`fs-disc-${frontSpeakers[i]?.id ?? i}`}>
          <mesh geometry={geo} castShadow receiveShadow>
            <meshPhysicalMaterial
              map={grillTexture}
              color={speakerGrillColor}
              metalness={speakerGrillMetalness}
              roughness={speakerGrillRoughness}
              clearcoat={speakerGrillClearcoat}
              clearcoatRoughness={speakerGrillClearcoatRoughness}
              reflectivity={speakerGrillReflectivity}
              specularIntensity={1}
              polygonOffset
              polygonOffsetFactor={-1}
              polygonOffsetUnits={-1}
            />
          </mesh>
        </group>
      ))}

      {/* Side button slot planes — transparent dark caps at cutout mouths */}
      {sideBtnSlotGeos.map((geo, i) => (
        <mesh key={`slot-${i}`} geometry={geo}>
          <meshPhysicalMaterial
            color={SHADOW_INSERT_COLOR}
            transparent
            opacity={SHADOW_INSERT_OPACITY}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Port insert backgrounds — dark material pushed slightly inward */}
      {portInsertGeos.map((geo, i) => (
        <mesh key={`port-insert-${i}`} geometry={geo}>
          <meshPhysicalMaterial
            color={SHADOW_INSERT_COLOR}
            transparent
            opacity={SHADOW_INSERT_OPACITY}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Port interior shells (rim) */}
      {portShellGeos.map((geo, i) => (
        <mesh key={`port-shell-${i}`} geometry={geo}>
          <meshPhysicalMaterial
            color={ccRimColor}
            metalness={ccRimMetalness}
            roughness={ccRimRoughness}
            clearcoat={ccRimClearcoat}
            clearcoatRoughness={ccRimClearcoatRoughness}
            reflectivity={ccRimReflectivity}
            specularIntensity={1}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}

      {/* Port tongues */}
      {portTongueGeos.map((geo, i) => (
        <mesh key={`port-tongue-${i}`} geometry={geo}>
          <meshPhysicalMaterial
            color={ccTongueColor}
            metalness={ccTongueMetalness}
            roughness={ccTongueRoughness}
            clearcoat={ccTongueClearcoat}
            clearcoatRoughness={ccTongueClearcoatRoughness}
            reflectivity={ccTongueReflectivity}
            specularIntensity={1}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}

      {/* Side button inserts */}
      {sideBtnGeos.map((geo, i) => (
        <mesh key={i} geometry={geo} castShadow receiveShadow>
          <meshPhysicalMaterial
            key={`btn-${buttonsNoiseIntensity > 0}-${buttonsNormalStrength > 0}-${buttonsNormalPattern}`}
            color={buttonsColor}
            metalness={buttonsMetalness}
            roughness={buttonsRoughness}
            envMapIntensity={materials.body.envMapIntensity}
            clearcoat={buttonsClearcoat}
            clearcoatRoughness={buttonsClearcoatRoughness}
            reflectivity={buttonsReflectivity}
            specularIntensity={1}
            roughnessMap={buttonsNoiseIntensity > 0 ? buttonsRoughnessMap : null}
            normalMap={buttonsNormalStrength > 0 ? buttonsNormalMap : null}
            normalScale={buttonsNormalScale}
          />
        </mesh>
      ))}

      {/* Mute switch slot planes — transparent dark caps at cutout mouths */}
      {muteSwitchSlotGeos.map((geo, i) => (
        <mesh key={`mute-slot-${i}`} geometry={geo}>
          <meshPhysicalMaterial
            color={SHADOW_INSERT_COLOR}
            transparent
            opacity={SHADOW_INSERT_OPACITY}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Mute switch inserts */}
      {muteSwitchInsertGeos.map((geo, i) => (
        <mesh key={`mute-ins-${i}`} geometry={geo} castShadow receiveShadow>
          <meshPhysicalMaterial
            key={`mute-${buttonsNoiseIntensity > 0}-${buttonsNormalStrength > 0}-${buttonsNormalPattern}`}
            color={buttonsColor}
            metalness={buttonsMetalness}
            roughness={buttonsRoughness}
            envMapIntensity={materials.body.envMapIntensity}
            clearcoat={buttonsClearcoat}
            clearcoatRoughness={buttonsClearcoatRoughness}
            reflectivity={buttonsReflectivity}
            specularIntensity={1}
            roughnessMap={buttonsNoiseIntensity > 0 ? buttonsRoughnessMap : null}
            normalMap={buttonsNormalStrength > 0 ? buttonsNormalMap : null}
            normalScale={buttonsNormalScale}
          />
        </mesh>
      ))}

      {/* Speaker grill slot planes — transparent dark caps at hole mouths */}
      {speakerGrillSlotGeos.map((geo, i) => (
        <mesh key={`grill-slot-${i}`} geometry={geo}>
          <meshPhysicalMaterial
            color={SHADOW_INSERT_COLOR}
            transparent
            opacity={SHADOW_INSERT_OPACITY}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Speaker grill mesh discs — textured with grill pattern */}
      {speakerGrillDiscGeos.map((geo, i) => (
        <mesh key={`grill-${i}`} geometry={geo} castShadow receiveShadow>
          <meshPhysicalMaterial
            map={grillTexture}
            color={speakerGrillColor}
            metalness={speakerGrillMetalness}
            roughness={speakerGrillRoughness}
            clearcoat={speakerGrillClearcoat}
            clearcoatRoughness={speakerGrillClearcoatRoughness}
            reflectivity={speakerGrillReflectivity}
            specularIntensity={1}
          />
        </mesh>
      ))}

      {/* Screw discs — textured with screw head image */}
      {screwDiscGeos.map((geo, i) => (
        <mesh key={`screw-${i}`} geometry={geo} castShadow receiveShadow>
          <meshPhysicalMaterial
            map={screwTexture}
            transparent
            alphaTest={0.5}
            color={screwsColor}
            metalness={screwsMetalness}
            roughness={screwsRoughness}
            clearcoat={screwsClearcoat}
            clearcoatRoughness={screwsClearcoatRoughness}
            reflectivity={screwsReflectivity}
            specularIntensity={1}
          />
        </mesh>
      ))}

      {/* SIM card slot caps — transparent dark at cutout mouths */}
      {simCardSlotSlotGeos.map((geo, i) => (
        <mesh key={`sim-slot-${i}`} geometry={geo}>
          <meshPhysicalMaterial
            color={SHADOW_INSERT_COLOR}
            transparent
            opacity={SHADOW_INSERT_OPACITY}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* SIM card tray inserts */}
      {simCardSlotTrayGeos.map((geo, i) => (
        <mesh key={`sim-tray-${i}`} geometry={geo} castShadow receiveShadow>
          <meshPhysicalMaterial
            key={`sim-${buttonsNoiseIntensity > 0}-${buttonsNormalStrength > 0}-${buttonsNormalPattern}`}
            color={buttonsColor}
            metalness={buttonsMetalness}
            roughness={buttonsRoughness}
            envMapIntensity={materials.body.envMapIntensity}
            clearcoat={buttonsClearcoat}
            clearcoatRoughness={buttonsClearcoatRoughness}
            reflectivity={buttonsReflectivity}
            specularIntensity={1}
            roughnessMap={buttonsNoiseIntensity > 0 ? buttonsRoughnessMap : null}
            normalMap={buttonsNormalStrength > 0 ? buttonsNormalMap : null}
            normalScale={buttonsNormalScale}
          />
        </mesh>
      ))}
    </group>
  )
}

export default DeviceMeshV2
